/**
 * Fuel price list — prices for fills the fuel source did not price (e.g.
 * depot AdBlue that Jigsaw records at £0). A price holds from its date until
 * a later entry for the same fuel and site. Nothing is edited: a new price or
 * a correction is a new entry, and the replaced one stays in the history.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListOrdered, Loader2, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select } from '../ui/select';
import { cn } from '../../lib/utils';
import {
  addFuelPriceEntry,
  fetchFuelPriceList,
  fuelPriceEntryError,
  FUEL_PRICE_LIST_FUEL_TYPES,
  type FuelPriceListEntry,
  type FuelPriceListFuelType,
} from '../../utils/fuelPriceList';

const FUEL_LABELS: Record<FuelPriceListFuelType, string> = {
  diesel: 'Diesel',
  adblue: 'AdBlue',
  petrol: 'Petrol',
  hvo: 'HVO',
  other: 'Other',
};

const INPUT_CLASS =
  'bg-background border border-border text-white rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-chart-2/50';

interface FuelPriceListPanelProps {
  useMockData?: boolean;
  onSaved?: () => void;
}

export default function FuelPriceListPanel({ useMockData = false, onSaved }: FuelPriceListPanelProps) {
  const [entries, setEntries] = useState<FuelPriceListEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fuelType, setFuelType] = useState<FuelPriceListFuelType>('adblue');
  const [site, setSite] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [rateInput, setRateInput] = useState('');
  const [provenance, setProvenance] = useState<'Asserted' | 'Estimated'>('Asserted');
  const [basis, setBasis] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const reload = useCallback(async () => {
    if (useMockData) return;
    setLoading(true);
    setLoadError(null);
    try {
      setEntries(await fetchFuelPriceList());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [useMockData]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const knownSites = useMemo(
    () => [...new Set(entries.map((e) => e.site_name).filter((s): s is string => Boolean(s)))].sort(),
    [entries],
  );

  const handleSave = async () => {
    const input = {
      fuel_type: fuelType,
      site_name: site,
      effective_from: effectiveFrom,
      pence_per_litre: Number(rateInput),
      provenance_class: provenance,
      basis,
    };
    const problem = fuelPriceEntryError(input);
    if (problem) {
      setMessage({ kind: 'error', text: problem });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await addFuelPriceEntry(input);
      setMessage({
        kind: 'ok',
        text: `Queued: ${FUEL_LABELS[fuelType]} ${site.trim() || 'all sites'} ${input.pence_per_litre}p/L from ${effectiveFrom}.`,
      });
      setRateInput('');
      setBasis('');
      onSaved?.();
      // Ingest is async; reload shortly after it is queued.
      window.setTimeout(() => void reload(), 3000);
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card/50 rounded-lg border border-border p-5 flex flex-col min-h-0 h-full">
      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
        <ListOrdered className="w-4 h-4 text-chart-2" />
        Fuel price list
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => void reload()}
          disabled={useMockData || loading}
          title="Reload the price list from ZeeCore"
          className="ml-auto font-semibold"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </Button>
      </h4>
      <p className="text-xs text-muted-foreground mt-1 mb-3">
        Prices for fills the fuel source did not price, such as depot AdBlue. A price holds from its
        date until a later one for the same fuel and site. Saving never changes an earlier entry.
      </p>

      <div className="flex flex-wrap gap-2 items-end">
        <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground min-w-28">
          Fuel
          <Select
            value={fuelType}
            onChange={(e) => setFuelType(e.target.value as FuelPriceListFuelType)}
            options={FUEL_PRICE_LIST_FUEL_TYPES.map((t) => ({ value: t, label: FUEL_LABELS[t] }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground flex-1 min-w-32">
          Site (blank = all sites)
          <input
            type="text"
            list="fuel-price-list-sites"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            placeholder="e.g. Bedford"
            className={INPUT_CLASS}
          />
          <datalist id="fuel-price-list-sites">
            {knownSites.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground min-w-32">
          Applies from
          <input
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground w-24">
          Price (p/L)
          <input
            type="text"
            inputMode="decimal"
            value={rateInput}
            onChange={(e) => setRateInput(e.target.value)}
            placeholder="e.g. 36"
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground min-w-36">
          Price is
          <Select
            value={provenance}
            onChange={(e) => setProvenance(e.target.value as 'Asserted' | 'Estimated')}
            options={[
              { value: 'Asserted', label: 'Confirmed (invoice / supplier)' },
              { value: 'Estimated', label: 'An estimate' },
            ]}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground flex-1 min-w-48">
          {provenance === 'Estimated' ? 'How was it estimated?' : 'Source (optional)'}
          <input
            type="text"
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            placeholder={provenance === 'Estimated' ? 'e.g. Duxford AdBlue pump price' : 'e.g. AdBlue delivery invoice 1234'}
            className={INPUT_CLASS}
          />
        </label>
        <Button
          variant="default"
          size="sm"
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || useMockData}
          className="bg-chart-2 hover:bg-chart-2 font-semibold shrink-0"
        >
          {saving ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Saving…
            </>
          ) : (
            'Add price'
          )}
        </Button>
      </div>

      {message && (
        <p className={cn('text-xs mt-2', message.kind === 'error' ? 'text-destructive' : 'text-success')}>
          {message.text}
        </p>
      )}
      {loadError && <p className="text-xs mt-2 text-destructive">{loadError}</p>}

      <div className="mt-4 max-h-price-list-5 overflow-y-auto overflow-x-auto rounded-md border border-border/60">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-background z-raised">
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border h-8">
              <th className="py-0 pr-2 pl-2 font-semibold">Fuel</th>
              <th className="py-0 pr-2 font-semibold">Site</th>
              <th className="py-0 pr-2 font-semibold">From</th>
              <th className="py-0 pr-2 font-semibold text-right">p/L</th>
              <th className="py-0 pr-2 font-semibold">Price is</th>
              <th className="py-0 pr-2 font-semibold">Basis</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-4 text-muted-foreground text-center">
                  {loading ? 'Loading…' : 'No prices in the list yet'}
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <tr
                  key={e.event_id}
                  className={cn('border-b border-border/80 h-9', e.corrected && 'opacity-50')}
                  title={e.corrected ? 'Replaced by a newer entry for the same date' : undefined}
                >
                  <td className="py-0 pr-2 pl-2 text-foreground align-middle">{FUEL_LABELS[e.fuel_type] ?? e.fuel_type}</td>
                  <td className="py-0 pr-2 text-foreground align-middle">{e.site_name ?? 'All sites'}</td>
                  <td className="py-0 pr-2 text-foreground align-middle tabular-nums">{e.effective_from}</td>
                  <td className={cn('py-0 pr-2 text-right tabular-nums text-white font-medium align-middle', e.corrected && 'line-through')}>
                    {e.pence_per_litre.toFixed(1)}
                  </td>
                  <td className="py-0 pr-2 align-middle">
                    <Badge className="font-bold" provenance={e.provenance_class === 'Estimated' ? 'inferred' : 'declared'}>
                      {e.corrected ? 'Replaced' : e.provenance_class === 'Estimated' ? 'Estimate' : 'Confirmed'}
                    </Badge>
                  </td>
                  <td className="py-0 pr-2 text-muted-foreground align-middle max-w-64 truncate" title={e.basis}>
                    {e.basis ?? ''}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
